import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from '@material-ui/core';
import FileIcon from '@material-ui/icons/Description';
import { withStyles } from '@material-ui/styles';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@material-ui/icons/Delete';

// SERVICES
import {
  getOrderAttachments,
  getReportAttachments,
  removeReportAttachment,
  uploadReportAttachment,
} from '../../../../../services';

const styles = theme => ({
  container: {
    flex: 1,
    backgroundColor: 'white',
    padding: 20,
    '& > *': {
      alignSelf: 'stretch',
    },
    '& h2': {
      textAlign: 'left',
    },
  },
  dropzone: {
    backgroundColor: '#F8F8F8',
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.4)',
    padding: 20,
    textAlign: 'center',
  },
  dialogTitle: {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e1e1e1',
  },
  dialogActions: {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e1e1e1',
  },
  cancelButton: {
    color: '#f44336',
  },
  removeButton: {
    color: '#f44336',
  },
  circularProgress: {
    padding: '6px 8px',
  },
  reportAttachmentList: {
    paddingLeft: 0,
    listStyle: 'none',
  },
  reportAttachmentListItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  fileLink: {
    display: 'flex',
    flexDirection: 'row',
    color: 'var(--jmi-blue)',
  },
  uploadButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  errorMessage: { color: '#c33' },

  deleteIcon: {
    color: 'var(--jmi-red)',
    borderRadius: '50%',
    padding: 5,
    cursor: 'pointer',
    marginRight: 10,

    '&:hover': {
      background: 'rgba(0,0,0,0.1)',
    },

    '&:active': {
      background: 'rgba(0,0,0,0.2)',
    },
  },
  deleteIconDisabled: {
    color: 'gray',
    opacity: 0.3,
    cursor: 'not-allowed',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },

  buttonDecorator: {
    marginLeft: 6,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: 'red',
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export const ReportAttachmentsMenuItem = withStyles(styles)(
  ({
    classes,
    orderLineId,
    readonly = false,
    onClose,
    reportAttachments,
    setReportAttachments,
    adminAttachments,
    setAdminAttachments,
    totalAttachments,
    loadingAttachments,
  }) => {
    const [files, setFiles] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [removingFile, setRemovingFile] = useState(false);
    const [errors, setErrors] = useState([]);

    const { getRootProps, getInputProps } = useDropzone({
      accept: '.pdf',
      onDrop: acceptedFiles => {
        setFiles(
          acceptedFiles.map(file =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            }),
          ),
        );
      },
    });

    const submitFiles = async () => {
      setSubmitting(true);

      const uploadFiles = files.map(file => uploadFile(file));
      const newReportAttachments = await Promise.all(uploadFiles);
      setReportAttachments([...reportAttachments, ...newReportAttachments]);
      setFiles([]);
      setSubmitting(false);
    };

    const uploadFile = async file => {
      try {
        const reportAttachment = await uploadReportAttachment(file, orderLineId);
        return reportAttachment;
      } catch (error) {
        const errorMessage = error.message
          ? `${file.path}: ${error.message}`
          : `${file.path}: Failed to upload file. Please check your connection and try again.`;
        setErrors([...errors, errorMessage]);
      }
    };

    const removeFile = async reportAttachmentId => {
      setRemovingFile(true);
      try {
        const reportAttachment = await removeReportAttachment(reportAttachmentId);
        const updateReportAttachments = reportAttachments.filter(ra => ra.reportAttachmentId !== reportAttachmentId);
        setReportAttachments(updateReportAttachments);
      } catch (error) {
        const errorMessage = error.message
          ? error.message
          : 'Failed to remove file. Please check your connection and try again.';
        setErrors([...errors, errorMessage]);
      } finally {
        setRemovingFile(false);
      }
    };

    const handleUploadCancel = () => {
      setFiles([]);
    };

    const handleDialogOpen = () => {
      setDialogOpen(true);
    };

    const handleDialogClose = () => {
      setDialogOpen(false);

      !!onClose && onClose();
    };

    const renderAttachment = (attachment, attachmentIndex, { fields, removeAttachment, removable }) => (
      <li key={attachment[fields.id]} className={classes.reportAttachmentListItem}>
        {!readonly && (
          <DeleteIcon
            //

            className={classnames(classes.deleteIcon, !removable && classes.deleteIconDisabled)}
            onClick={removable ? () => removeAttachment(attachment[fields.id]) : null}
          />
        )}

        <a
          //
          href={attachment[fields.location]}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.fileLink}
        >
          <FileIcon />
          {attachment[fields.name]}
        </a>
      </li>
    );

    const renderAdminAttachment = (orderAttachment, orderAttachmentIndex) => {
      return renderAttachment(orderAttachment, orderAttachmentIndex, {
        fields: {
          id: 'orderAttachmentId',
          location: 'location',
          name: 'name',
        },
        removable: false,
      });
    };

    const renderReportAttachment = (reportAttachment, reportAttachmentIndex) => {
      return renderAttachment(reportAttachment, reportAttachmentIndex, {
        fields: {
          id: 'reportAttachmentId',
          location: 'location',
          name: 'name',
        },
        removeAttachment: removeFile,
        removable: true,
      });
    };

    return (
      <React.Fragment>
        <MenuItem onClick={handleDialogOpen}>
          View/Upload Attachment(s)
          {!!totalAttachments && <span className={classes.buttonDecorator}>{totalAttachments}</span>}
        </MenuItem>

        <Dialog
          //
          maxWidth="md"
          fullWidth
          open={dialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
            Attachments
          </DialogTitle>

          <DialogContent>
            <main className={classnames('col-start-center', classes.container)}>
              {!readonly && (
                <>
                  <section
                    style={{
                      marginBottom: 8,
                      // textAlign: 'center'
                    }}
                  >
                    Upload report documents to attach them to the client PDF output.
                  </section>

                  <div {...getRootProps({ className: classes.dropzone })}>
                    <input {...getInputProps()} />
                    <p>Drag PDF files here or click to select files</p>
                  </div>

                  {!!files.length && (
                    <section>
                      <h4>Files to Upload</h4>
                      <ul>
                        {files.map(file => (
                          <li key={file.path}>
                            {file.path} - {file.size} bytes
                          </li>
                        ))}
                      </ul>
                      <div className={classes.uploadButtonContainer}>
                        <Button
                          onClick={handleUploadCancel}
                          disabled={submitting || loadingAttachments || removingFile}
                          className={classes.cancelButton}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          disabled={submitting || loadingAttachments || removingFile}
                          onClick={submitFiles}
                        >
                          {submitting ? (
                            <CircularProgress color="primary" size={18} className={classes.circularProgress} />
                          ) : (
                            'Upload'
                          )}
                        </Button>
                      </div>
                    </section>
                  )}
                </>
              )}

              <section className={classes.existingFiles}>
                <h2 style={{ marginBottom: 0 }}>Report Attachments</h2>
                <div style={{ textAlign: 'left', color: 'rgba(0,0,0,0.5)' }}>
                  Uploaded via QA portal (attached to client PDF output)
                </div>

                {loadingAttachments ? (
                  <CircularProgress color="primary" size={18} className={classes.circularProgress} />
                ) : !!reportAttachments.length ? (
                  <ul className={classes.reportAttachmentList}>{reportAttachments.map(renderReportAttachment)}</ul>
                ) : (
                  <p>No report attachments</p>
                )}

                <div style={{ height: 2, background: 'rgba(0,0,0,0.1)', borderRadius: 2, margin: '50px 0' }} />

                <h2 style={{ marginBottom: 0 }}>Admin Attachments</h2>
                <div style={{ textAlign: 'left', color: 'rgba(0,0,0,0.5)' }}>
                  Visible to all BlueSky users (not sent to client)
                </div>

                {loadingAttachments ? (
                  <CircularProgress color="primary" size={18} className={classes.circularProgress} />
                ) : !!adminAttachments.length ? (
                  <ul className={classes.reportAttachmentList}>{adminAttachments.map(renderAdminAttachment)}</ul>
                ) : (
                  <p>No order attachments</p>
                )}
              </section>
              <section>
                {!!errors.length && errors.map(error => <p className={classes.errorMessage}>{error}</p>)}
              </section>
            </main>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              color="primary"
              onClick={handleDialogClose}
              disabled={submitting || loadingAttachments || removingFile}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  },
);

ReportAttachmentsMenuItem.propTypes = {};
ReportAttachmentsMenuItem.defaultProps = {};
